import React, { useState } from "react";
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import markerIconPng from "leaflet/dist/images/marker-icon.png";
import { Icon } from "leaflet";
import { OpenStreetMapProvider } from "leaflet-geosearch";
import { Crosshair, Search, X } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { getMapPickerPosition } from "../../utils/mapPickerPosition";

export type GeoPoint = { type: "Point"; coordinates: [number, number] };

interface MapPickerProps {
  value?: GeoPoint | null;
  onChange: (point: GeoPoint | null) => void;
  onAddressFound?: (label: string) => void;
}

const markerIcon = new Icon({ iconUrl: markerIconPng, iconSize: [25, 41], iconAnchor: [12, 41] });
const DEFAULT_CENTER: [number, number] = [46.6, 2.5];
const provider = new OpenStreetMapProvider();

const ClickHandler = ({ onPick }: { onPick: (lat: number, lng: number) => void }) => {
  useMapEvents({ click: e => onPick(e.latlng.lat, e.latlng.lng) });
  return null;
};

const Recenter = ({ position }: { position: [number, number] | null }) => {
  const map = useMap();
  React.useEffect(() => {
    if (position) map.setView(position, Math.max(map.getZoom(), 14));
  }, [map, position]);
  return null;
};

/** Click on the map, search an address or use the current position to set a GPS point. */
export const MapPicker: React.FC<MapPickerProps> = ({ value, onChange, onAddressFound }) => {
  const [search, setSearch] = useState("");
  const [searching, setSearching] = useState(false);
  const position = getMapPickerPosition(value);

  const pick = (lat: number, lng: number) => onChange({ type: "Point", coordinates: [lng, lat] });

  const runSearch = async () => {
    if (!search.trim()) return;
    setSearching(true);
    try {
      const results = await provider.search({ query: search });
      if (results[0]) {
        pick(results[0].y, results[0].x);
        onAddressFound?.(results[0].label);
      }
    } finally {
      setSearching(false);
    }
  };

  const locate = () => navigator.geolocation?.getCurrentPosition(p => pick(p.coords.latitude, p.coords.longitude));

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); runSearch(); } }}
          placeholder="Rechercher une adresse…"
        />
        <Button type="button" variant="secondary" size="icon" onClick={runSearch} disabled={searching} aria-label="Rechercher"><Search className="h-4 w-4" /></Button>
        <Button type="button" variant="secondary" size="icon" onClick={locate} aria-label="Ma position"><Crosshair className="h-4 w-4" /></Button>
        {position && <Button type="button" variant="ghost" size="icon" onClick={() => onChange(null)} aria-label="Retirer le point"><X className="h-4 w-4" /></Button>}
      </div>
      <div className="h-56 overflow-hidden rounded-md border">
        <MapContainer center={position || DEFAULT_CENTER} zoom={position ? 14 : 5} style={{ height: "100%", width: "100%" }}>
          <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <ClickHandler onPick={pick} />
          <Recenter position={position} />
          {position && <Marker position={position} icon={markerIcon} />}
        </MapContainer>
      </div>
      <p className="text-xs text-muted-foreground">
        {position ? `Point GPS : ${position[0].toFixed(5)}, ${position[1].toFixed(5)}` : "Cliquez sur la carte pour placer le point de rendez-vous."}
      </p>
    </div>
  );
};
